import React, { useState, useRef, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import Quill from 'quill';
import 'quill/dist/quill.snow.css';

const BASE_URL = process.env.REACT_APP_BASE_URL;

const AdminEditCareer = () => {
  const [jobCode, setJobCode] = useState('');
  const [jobTitle, setJobTitle] = useState('');
  const [location, setLocation] = useState('South Australia');
  const [jobType, setJobType] = useState('Disability Support Worker');
  const [description, setDescription] = useState('');
  const [status, setStatus] = useState('Active');
  
  const editorRef = useRef(null);
  const quillRef = useRef(null);
  const navigate = useNavigate();
  const { id } = useParams();

  const locations = ['South Australia', 'Victoria', 'Queensland'];
  const jobTypes = ['Disability Support Worker', 'Other Vacancies'];
  const statusOptions = ['Active', 'Closed', 'On Hold'];

  useEffect(() => {
    const fetchCareer = async () => {
      try {
        const response = await fetch(`${BASE_URL}/api/careers/${id}`, {
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('adminToken')}`
          }
        });
        if (response.ok) {
          const data = await response.json();
          setJobCode(data.jobCode);
          setJobTitle(data.jobTitle);
          setLocation(data.location);
          setJobType(data.jobType);
          setStatus(data.status || 'Active');
          
          // Initialize Quill with content after data is fetched
          if (quillRef.current) {
            quillRef.current.root.innerHTML = data.description;
          }
        }
      } catch (error) {
        console.error('Error fetching career:', error);
      }
    };

    fetchCareer();
  }, [id]);

  useEffect(() => {
    if (editorRef.current && !quillRef.current) {
      quillRef.current = new Quill(editorRef.current, {
        theme: 'snow',
        modules: {
          toolbar: [
            [{ 'header': [1, 2, 3, false] }],
            ['bold', 'italic', 'underline'],
            [{ 'list': 'ordered'}, { 'list': 'bullet' }],
            ['clean']
          ]
        },
        placeholder: 'Write job description...',
      });

      const editor = editorRef.current.querySelector('.ql-editor');
      editor.style.minHeight = '300px';
      
      quillRef.current.on('text-change', () => {
        setDescription(quillRef.current.root.innerHTML);
      });
    }
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    const career = {
      jobCode,
      jobTitle,
      location,
      jobType,
      description: quillRef.current.root.innerHTML,
      status
    };

    try {
      const response = await fetch(`${BASE_URL}/api/admin/updatecareer/${id}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('adminToken')}`
        },
        body: JSON.stringify(career)
      });
      if (response.ok) {
        navigate('/admin/dashboard');
      }
    } catch (error) {
      console.error('Error updating career posting:', error);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 py-12">
      <div className="max-w-4xl mx-auto p-8 bg-white shadow-2xl rounded-2xl">
        <h2 className="text-4xl font-bold mb-10 text-center text-gray-800">
          Edit Job Posting
        </h2>

        <form onSubmit={handleSubmit} className="space-y-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <label className="block font-medium mb-2 text-gray-700">Job Code</label>
              <input
                type="text"
                value={jobCode}
                onChange={(e) => setJobCode(e.target.value)}
                className="w-full p-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-indigo-500"
                required
              />
            </div>
            <div>
              <label className="block font-medium mb-2 text-gray-700">Job Title</label>
              <input
                type="text"
                value={jobTitle}
                onChange={(e) => setJobTitle(e.target.value)}
                className="w-full p-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-indigo-500"
                required
              />
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <div>
              <label className="block font-medium mb-2 text-gray-700">Location</label>
              <select
                value={location}
                onChange={(e) => setLocation(e.target.value)}
                className="w-full p-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-indigo-500"
              >
                {locations.map(loc => (
                  <option key={loc} value={loc}>{loc}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="block font-medium mb-2 text-gray-700">Job Type</label>
              <select
                value={jobType}
                onChange={(e) => setJobType(e.target.value)}
                className="w-full p-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-indigo-500"
              >
                {jobTypes.map(type => (
                  <option key={type} value={type}>{type}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="block font-medium mb-2 text-gray-700">Status</label>
              <select
                value={status}
                onChange={(e) => setStatus(e.target.value)}
                className="w-full p-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-indigo-500"
              >
                {statusOptions.map(option => (
                  <option key={option} value={option}>{option}</option>
                ))}
              </select>
            </div>
          </div>

          <div>
            <label className="block font-medium mb-2 text-gray-700">Job Description</label>
            <div ref={editorRef} className="bg-white border border-gray-300 rounded-xl"></div>
          </div>

          <div className="flex gap-4">
            <button
              type="submit"
              className="flex-1 py-4 bg-indigo-600 text-white text-xl font-semibold rounded-xl
                       hover:bg-indigo-700 transition-all duration-200"
            >
              Update Job Posting
            </button>
            <button
              type="button"
              onClick={() => navigate('/admin/dashboard')}
              className="flex-1 py-4 bg-gray-600 text-white text-xl font-semibold rounded-xl
                       hover:bg-gray-700 transition-all duration-200"
            >
              Cancel
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default AdminEditCareer;
